import React, { useState, useEffect } from "react";
import { useSettings } from "../../context/SettingsContext";
import { useSeason } from "../../context/SeasonContext";
import {
  Save,
  DollarSign,
  Users,
  Calendar,
  Clock,
  FileText,
  ArrowRightLeft,
  Link,
  CheckCircle,
  AlertCircle,
} from "lucide-react";

const LeagueSettings = () => {
  const { settings, updateSettings } = useSettings();
  // No local season selector here — this reads the SAME selectedSeason that
  // the Admin Dashboard's header switcher controls (both consume
  // SeasonContext), so switching seasons anywhere in the dashboard updates
  // this page automatically.
  const { activeSeason, selectedSeason } = useSeason();
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // null | 'success' | 'error'
  const [saveError, setSaveError] = useState("");
  const [activeTab, setActiveTab] = useState("pricing");
  const [urlError, setUrlError] = useState("");

  const isLocked = selectedSeason?.status === "completed";

  useEffect(() => {
    if (settings) {
      setFormData({
        // Pricing
        early_bird_price: settings.early_bird_price,
        regular_price: settings.regular_price,
        additional_player_fee: settings.additional_player_fee,
        ref_fee_coverage: settings.ref_fee_coverage,

        // Dates
        early_bird_deadline: settings.early_bird_deadline,
        registration_deadline: settings.registration_deadline,
        season_start_date: settings.season_start_date,

        // Team Requirements
        min_players: settings.min_players,
        max_players: settings.max_players,
        base_player_count: settings.base_player_count,
        min_age: settings.min_age,
        max_non_muslim_players: settings.max_non_muslim_players,

        // Game Settings
        game_duration_minutes: settings.game_duration_minutes,
        quarters: settings.quarters,
        refs_per_game: settings.refs_per_game,
        regular_season_games: settings.regular_season_games,
        games_per_week: settings.games_per_week,
        game_time_slot_1: settings.game_time_slot_1,
        game_time_slot_2: settings.game_time_slot_2,
        game_day: settings.game_day,

        // Trades
        trades_enabled: settings.trades_enabled || false,
        trade_deadline: settings.trade_deadline || "",

        // Rules
        rules: settings.rules || {},
        rules_url: settings.rules_url || "",
      });
    }
  }, [settings]);

  const isValidUrl = (value) => {
    if (!value) return true; // empty is allowed (optional field)
    try {
      const url = new URL(value);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "rules_url") {
      setUrlError(
        value && !isValidUrl(value)
          ? "Please enter a valid URL starting with http:// or https://"
          : "",
      );
    }
  };

  const handleRuleChange = (rule, value) => {
    setFormData((prev) => ({
      ...prev,
      rules: { ...prev.rules, [rule]: value },
    }));
  };

  const handleSave = async () => {
    if (isLocked) return;
    if (urlError) return; // block save if URL is invalid
    setSaving(true);
    setSaveStatus(null);
    setSaveError("");
    const result = await updateSettings(formData);

    if (result.success) {
      setSaveStatus("success");
      setTimeout(() => setSaveStatus(null), 4000);
    } else {
      setSaveStatus("error");
      setSaveError(result.error?.message || "Unknown error");
    }

    setSaving(false);
  };

  if (!settings) {
    return <div className="text-center py-12">Loading settings...</div>;
  }

  const tabs = [
    { id: "pricing", label: "Pricing & Fees", icon: DollarSign },
    { id: "dates", label: "Important Dates", icon: Calendar },
    { id: "team", label: "Team Requirements", icon: Users },
    { id: "games", label: "Game Settings", icon: Clock },
    { id: "trades", label: "Player Trades", icon: ArrowRightLeft },
    { id: "rules", label: "League Rules", icon: FileText },
  ];

  return (
    <div className="space-y-6">
      {/* Save feedback banner */}
      {saveStatus === "success" && (
        <div className="flex items-center gap-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300 rounded-lg px-4 py-3 text-sm font-medium">
          <CheckCircle size={16} className="flex-shrink-0" />
          Settings saved successfully!
        </div>
      )}
      {saveStatus === "error" && (
        <div className="flex items-center gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 rounded-lg px-4 py-3 text-sm font-medium">
          <AlertCircle size={16} className="flex-shrink-0" />
          Error saving settings: {saveError}
        </div>
      )}

      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold dark:text-white">
              League Settings
            </h1>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <span className="text-gray-600 dark:text-gray-400 text-sm">
                Editing settings for:
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {selectedSeason?.name ?? "—"}
                {selectedSeason &&
                  selectedSeason.id === activeSeason?.id &&
                  " (Live)"}
                {selectedSeason?.status === "registration" && " (Registration)"}
              </span>
              {isLocked && (
                <span className="text-xs font-semibold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full">
                  Locked — completed season
                </span>
              )}
              <span className="text-xs text-gray-400 dark:text-gray-500">
                (use the season switcher above to change)
              </span>
            </div>
          </div>

          <button
            onClick={handleSave}
            disabled={saving || isLocked || !!urlError}
            className="flex-shrink-0 flex items-center gap-2 bg-orange-600 text-white px-5 py-2.5 rounded-lg hover:bg-orange-700 transition disabled:opacity-50"
          >
            <Save size={20} />
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="border-b dark:border-gray-700 flex overflow-x-auto scrollbar-thin">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 sm:px-6 py-3 font-semibold whitespace-nowrap text-sm ${
                  activeTab === tab.id
                    ? "border-b-2 border-orange-600 text-orange-600 dark:text-orange-400"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="p-6">
          {/* Pricing & Fees Tab */}
          {activeTab === "pricing" && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold dark:text-white">
                Pricing & Fees
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block font-semibold mb-2 dark:text-white">
                    Early Bird Price
                  </label>
                  <div className="flex items-center">
                    <span className="mr-2 text-gray-600 dark:text-gray-400">
                      $
                    </span>
                    <input
                      type="number"
                      value={formData.early_bird_price || ""}
                      onChange={(e) =>
                        handleChange(
                          "early_bird_price",
                          parseFloat(e.target.value),
                        )
                      }
                      className="flex-1 px-4 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-semibold mb-2 dark:text-white">
                    Regular Price
                  </label>
                  <div className="flex items-center">
                    <span className="mr-2 text-gray-600 dark:text-gray-400">
                      $
                    </span>
                    <input
                      type="number"
                      value={formData.regular_price || ""}
                      onChange={(e) =>
                        handleChange(
                          "regular_price",
                          parseFloat(e.target.value),
                        )
                      }
                      className="flex-1 px-4 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-semibold mb-2 dark:text-white">
                    Additional Player Fee
                  </label>
                  <div className="flex items-center">
                    <span className="mr-2 text-gray-600 dark:text-gray-400">
                      $
                    </span>
                    <input
                      type="number"
                      value={formData.additional_player_fee || ""}
                      onChange={(e) =>
                        handleChange(
                          "additional_player_fee",
                          parseFloat(e.target.value),
                        )
                      }
                      className="flex-1 px-4 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Per player beyond base count
                  </p>
                </div>

                <div>
                  <label className="block font-semibold mb-2 dark:text-white">
                    Ref Fee Coverage (Optional)
                  </label>
                  <div className="flex items-center">
                    <span className="mr-2 text-gray-600 dark:text-gray-400">
                      $
                    </span>
                    <input
                      type="number"
                      value={formData.ref_fee_coverage || ""}
                      onChange={(e) =>
                        handleChange(
                          "ref_fee_coverage",
                          parseFloat(e.target.value),
                        )
                      }
                      className="flex-1 px-4 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    One-time fee to cover all ref costs
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Important Dates Tab */}
          {activeTab === "dates" && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold dark:text-white">
                Important Dates
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block font-semibold mb-2 dark:text-white">
                    Early Bird Deadline
                  </label>
                  <input
                    type="date"
                    value={formData.early_bird_deadline || ""}
                    onChange={(e) =>
                      handleChange("early_bird_deadline", e.target.value)
                    }
                    className="w-full px-4 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block font-semibold mb-2 dark:text-white">
                    Registration Deadline
                  </label>
                  <input
                    type="date"
                    value={formData.registration_deadline || ""}
                    onChange={(e) =>
                      handleChange("registration_deadline", e.target.value)
                    }
                    className="w-full px-4 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block font-semibold mb-2 dark:text-white">
                    Season Start Date
                  </label>
                  <input
                    type="date"
                    value={formData.season_start_date || ""}
                    onChange={(e) =>
                      handleChange("season_start_date", e.target.value)
                    }
                    className="w-full px-4 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Team Requirements Tab */}
          {activeTab === "team" && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold dark:text-white">
                Team Requirements
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block font-semibold mb-2 dark:text-white">
                    Minimum Players
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.min_players || ""}
                    onChange={(e) =>
                      handleChange("min_players", parseInt(e.target.value))
                    }
                    className="w-full px-4 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block font-semibold mb-2 dark:text-white">
                    Maximum Players
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.max_players || ""}
                    onChange={(e) =>
                      handleChange("max_players", parseInt(e.target.value))
                    }
                    className="w-full px-4 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block font-semibold mb-2 dark:text-white">
                    Base Player Count
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.base_player_count || ""}
                    onChange={(e) =>
                      handleChange(
                        "base_player_count",
                        parseInt(e.target.value),
                      )
                    }
                    className="w-full px-4 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Number of players included in base price
                  </p>
                </div>

                <div>
                  <label className="block font-semibold mb-2 dark:text-white">
                    Minimum Age
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.min_age || ""}
                    onChange={(e) =>
                      handleChange("min_age", parseInt(e.target.value))
                    }
                    className="w-full px-4 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Players must be this age or older
                  </p>
                </div>

                <div>
                  <label className="block font-semibold mb-2 dark:text-white">
                    Max Non-Muslim Players
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.max_non_muslim_players || ""}
                    onChange={(e) =>
                      handleChange(
                        "max_non_muslim_players",
                        parseInt(e.target.value),
                      )
                    }
                    className="w-full px-4 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Game Settings Tab */}
          {activeTab === "games" && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold dark:text-white">
                Game Settings
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block font-semibold mb-2 dark:text-white">
                    Game Duration (minutes)
                  </label>
                  <input
                    type="number"
                    value={formData.game_duration_minutes || ""}
                    onChange={(e) =>
                      handleChange(
                        "game_duration_minutes",
                        parseInt(e.target.value),
                      )
                    }
                    className="w-full px-4 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block font-semibold mb-2 dark:text-white">
                    Number of Quarters
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.quarters || ""}
                    onChange={(e) =>
                      handleChange("quarters", parseInt(e.target.value))
                    }
                    className="w-full px-4 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block font-semibold mb-2 dark:text-white">
                    Refs Per Game
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.refs_per_game || ""}
                    onChange={(e) =>
                      handleChange("refs_per_game", parseInt(e.target.value))
                    }
                    className="w-full px-4 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block font-semibold mb-2 dark:text-white">
                    Regular Season Games
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.regular_season_games || ""}
                    onChange={(e) =>
                      handleChange(
                        "regular_season_games",
                        parseInt(e.target.value),
                      )
                    }
                    className="w-full px-4 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block font-semibold mb-2 dark:text-white">
                    Games Per Week
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.games_per_week || ""}
                    onChange={(e) =>
                      handleChange("games_per_week", parseInt(e.target.value))
                    }
                    className="w-full px-4 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block font-semibold mb-2 dark:text-white">
                    Game Day
                  </label>
                  <select
                    value={formData.game_day || ""}
                    onChange={(e) => handleChange("game_day", e.target.value)}
                    className="w-full px-4 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="Sunday">Sunday</option>
                    <option value="Monday">Monday</option>
                    <option value="Tuesday">Tuesday</option>
                    <option value="Wednesday">Wednesday</option>
                    <option value="Thursday">Thursday</option>
                    <option value="Friday">Friday</option>
                    <option value="Saturday">Saturday</option>
                  </select>
                </div>

                <div>
                  <label className="block font-semibold mb-2 dark:text-white">
                    First Time Slot
                  </label>
                  <input
                    type="time"
                    value={formData.game_time_slot_1 || ""}
                    onChange={(e) =>
                      handleChange("game_time_slot_1", e.target.value)
                    }
                    className="w-full px-4 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block font-semibold mb-2 dark:text-white">
                    Second Time Slot
                  </label>
                  <input
                    type="time"
                    value={formData.game_time_slot_2 || ""}
                    onChange={(e) =>
                      handleChange("game_time_slot_2", e.target.value)
                    }
                    className="w-full px-4 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block font-semibold mb-2 dark:text-white">
                    Third Time Slot
                  </label>
                  <input
                    type="time"
                    value={formData.game_time_slot_3 || ""}
                    onChange={(e) =>
                      handleChange("game_time_slot_3", e.target.value)
                    }
                    className="w-full px-4 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Player Trades Tab */}
          {activeTab === "trades" && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold dark:text-white">
                Player Trade Settings
              </h2>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-900 dark:text-blue-300">
                  <strong>Trade System:</strong> Enable player trades to allow
                  captains and players to request roster changes during the
                  season. Set a deadline to prevent trades after a certain date.
                  All trades require captain approval and final admin approval
                  before execution.
                </p>
              </div>

              <div className="space-y-6">
                {/* Enable/Disable Trades */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.trades_enabled || false}
                      onChange={(e) =>
                        handleChange("trades_enabled", e.target.checked)
                      }
                      className="w-6 h-6 text-orange-600 rounded focus:ring-orange-500 mt-1"
                    />
                    <div className="flex-1">
                      <span className="font-semibold text-lg dark:text-white block mb-1">
                        Enable Player Trades
                      </span>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Allow players and captains to request trades. When
                        disabled, all trade creation and approval is blocked.
                      </p>
                    </div>
                  </label>
                </div>

                {/* Trade Deadline */}
                <div>
                  <label className="block font-semibold mb-2 dark:text-white">
                    Trade Deadline
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.trade_deadline || ""}
                    onChange={(e) =>
                      handleChange("trade_deadline", e.target.value)
                    }
                    disabled={!formData.trades_enabled}
                    className="w-full px-4 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    {formData.trade_deadline
                      ? `Trades cannot be created or approved after ${new Date(formData.trade_deadline).toLocaleString()}`
                      : "No deadline set - trades allowed indefinitely (while enabled)"}
                  </p>
                </div>

                {/* Trade Workflow Info */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h3 className="font-semibold dark:text-white mb-3">
                    Trade Approval Workflow
                  </h3>
                  <ol className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                    <li className="flex items-start gap-2">
                      <span className="font-semibold text-orange-600 dark:text-orange-400">
                        1.
                      </span>
                      <span>
                        Player requests trade OR Captain proposes player swap
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-semibold text-orange-600 dark:text-orange-400">
                        2.
                      </span>
                      <span>Both team captains must approve the trade</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-semibold text-orange-600 dark:text-orange-400">
                        3.
                      </span>
                      <span>
                        Admin (Commissioner) executes or rejects the trade
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-semibold text-orange-600 dark:text-orange-400">
                        4.
                      </span>
                      <span>
                        Player rosters updated and movement logged to history
                      </span>
                    </li>
                  </ol>
                </div>

                {/* Historical Data Info */}
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <h3 className="font-semibold text-green-900 dark:text-green-400 mb-2">
                    ✓ Historical Data Protected
                  </h3>
                  <p className="text-sm text-green-800 dark:text-green-300">
                    All team changes are logged to the player team history
                    table. Past game stats and box scores remain accurate
                    regardless of current team assignments. Movement types
                    include: Trade, Free Agency Sign, Injury Release, Draft, and
                    Initial Registration.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* League Rules Tab */}
          {activeTab === "rules" && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold dark:text-white">
                League Rules & Features
              </h2>

              {/* Rules Document URL */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-5 border border-gray-200 dark:border-gray-600">
                <label className="block font-semibold mb-1 dark:text-white flex items-center gap-2">
                  <Link size={16} className="text-orange-500" />
                  League Rules Document URL
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  Link to the external rules document (e.g. Google Doc) shown in
                  the site footer and wherever league rules are referenced.
                  Leave blank to hide the link.
                </p>
                <input
                  type="url"
                  value={formData.rules_url || ""}
                  onChange={(e) => handleChange("rules_url", e.target.value)}
                  disabled={isLocked}
                  placeholder="https://docs.google.com/document/d/your-doc-id/edit"
                  className={`w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white transition-colors ${
                    urlError
                      ? "border-red-400 dark:border-red-500 focus:ring-red-400"
                      : "border-gray-300 dark:border-gray-600 focus:ring-orange-400"
                  } focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                />
                {urlError && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1.5">
                    <AlertCircle size={14} />
                    {urlError}
                  </p>
                )}
                {formData.rules_url && !urlError && (
                  <p className="mt-2 text-sm text-green-600 dark:text-green-400 flex items-center gap-1.5">
                    <CheckCircle size={14} />
                    Valid URL — will appear as "League Rules" in the site
                    footer.
                  </p>
                )}
              </div>

              {/* Feature toggles */}
              <div className="space-y-4">
                {[
                  { key: "jerseys_provided", label: "Jerseys Provided" },
                  { key: "videos_provided", label: "Videos/Photos Provided" },
                  { key: "all_star_game", label: "All-Star Game" },
                  { key: "islamic_reminders", label: "Islamic Reminders" },
                  { key: "playoff_guaranteed", label: "Guaranteed Playoffs" },
                  { key: "no_pro_players", label: "No Professional Players" },
                  { key: "no_d1_players", label: "No D1 College Players" },
                ].map((rule) => (
                  <label
                    key={rule.key}
                    className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  >
                    <input
                      type="checkbox"
                      checked={formData.rules?.[rule.key] || false}
                      onChange={(e) =>
                        handleRuleChange(rule.key, e.target.checked)
                      }
                      className="w-5 h-5 text-orange-600 rounded focus:ring-orange-500"
                    />
                    <span className="font-medium dark:text-white">
                      {rule.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save Button (Bottom) */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving || isLocked || !!urlError}
          className="flex items-center gap-2 bg-orange-600 text-white px-8 py-3 rounded-lg hover:bg-orange-700 transition disabled:opacity-50 text-lg font-semibold"
        >
          <Save size={24} />
          {saving ? "Saving Changes..." : "Save All Changes"}
        </button>
      </div>
    </div>
  );
};

export default LeagueSettings;
